import type { ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import LegacyTable, { HeaderCell } from "@core/components/legacy-table";
import { Badge, Text, Title } from "rizzui";
import { ComplianceStatusBadge, type ComplianceState } from "./compliance-status-badge";

/**
 * The GST credit note (`006` FR-011, §13.5), laid out from the API's own fields
 * (`GET /api/billing/admin/credit-notes/{id}`) — `apps/admin`'s copy of
 * `apps/portal/components/credit-note-document.tsx`, kept in sync by hand the way `InvoiceDocument`
 * is (no shared component across the two design systems).
 *
 * `InvoiceDocument`'s sibling, in the same markup: a credit note has no basis rule and no
 * brand/category per line, so it is drawn here rather than fed through `InvoiceDocument` with
 * placeholders. Nothing is computed (constitution I): every figure is a field the API sent. The
 * supplier and recipient blocks print only what Billing recorded; a missing GSTIN prints in words,
 * never a dash. Same `data-slot` as the invoice, so the print stylesheet's A4 rules apply.
 */
export type CreditNoteDocumentLine = {
  id: string;
  skuName: string;
  hsnCode: string;
  gstRate: number | string;
  quantity: number | string;
  unitCode: string;
  taxableValue: number | string;
  taxAmount: number | string;
  lineTotal: number | string;
  /** D-39: returned quantity × the invoiced rate, before the invoice's trade discount. */
  grossAmount?: number | string | null;
  discountPercent?: number | string | null;
  /** D-39: the returned goods' share of the trade discount, as Billing stored it. */
  discount?: number | string | null;
};

export type CreditNoteDocumentData = {
  creditNoteNumber: string;
  financialYear: string;
  /** Plain ISO date. */
  issuedOn: string;
  reason: string;
  placeOfSupply: string;
  invoiceNumber?: string | null;
  supplierGstin?: string | null;
  supplierLegalName?: string | null;
  supplierAddress?: string | null;
  recipientGstin?: string | null;
  recipientLegalName?: string | null;
  recipientAddress?: string | null;
  irn: string | null;
  irnStatus: string;
  irnQr?: string | null;
  lines: CreditNoteDocumentLine[];
  taxableValue: number | string;
  cgst: number | string;
  sgst: number | string;
  igst: number | string;
  roundOff: number | string;
  grandTotal: number | string;
  /** Indian-grouped by the API, when it sent one. */
  grandTotalDisplay?: string | null;
  /** D-39: gross before the trade discount, and the discount itself. Absent or zero means none. */
  grossValue?: number | string | null;
  discount?: number | string | null;
};

/** D-39: whether the API reported a trade discount on this credit note — a read, not a derivation. */
const isDiscounted = (data: CreditNoteDocumentData) => data.discount != null && Number(data.discount) > 0;

const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const money = (value: number | string) => MONEY.format(typeof value === "number" ? value : Number(value));

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(iso));

const words = (status: string) => status.replaceAll("_", " ");

/** QR version 40 at level L holds 2,953 bytes; a longer payload is left as text rather than throwing the page. */
const QR_MAX_LENGTH = 2900;

const right = (value: ReactNode) => <span className="block text-right tabular-nums">{value}</span>;

/** D-39's two columns, after Unit, only on a discounted credit note. */
const discountColumns = [
  {
    title: <HeaderCell title="Amount" align="right" />,
    dataIndex: "grossAmount",
    key: "grossAmount",
    render: (_: unknown, record: Record<string, unknown>) => {
      const line = record as CreditNoteDocumentLine;
      return right(money(line.grossAmount ?? line.taxableValue));
    },
  },
  {
    title: <HeaderCell title="Discount" align="right" />,
    dataIndex: "discount",
    key: "discount",
    render: (_: unknown, record: Record<string, unknown>) => {
      const line = record as CreditNoteDocumentLine;
      return right(
        line.discountPercent != null && Number(line.discountPercent) > 0
          ? `${money(line.discount ?? 0)} (${String(line.discountPercent)}%)`
          : money(line.discount ?? 0),
      );
    },
  },
];

const lineColumns = [
  { title: "Item", dataIndex: "skuName", key: "skuName", render: (value: string) => <span className="font-medium">{value}</span> },
  { title: "HSN", dataIndex: "hsnCode", key: "hsnCode", render: (value: string) => <span className="font-mono text-xs">{value}</span> },
  { title: <HeaderCell title="GST rate" align="right" />, dataIndex: "gstRate", key: "gstRate", render: (value: number | string) => right(`${String(value)}%`) },
  { title: <HeaderCell title="Returned qty" align="right" />, dataIndex: "quantity", key: "quantity", render: (value: number | string) => right(String(value)) },
  { title: <HeaderCell title="Unit" align="right" />, dataIndex: "unitCode", key: "unitCode", render: (value: string) => right(value) },
  { title: <HeaderCell title="Taxable value" align="right" />, dataIndex: "taxableValue", key: "taxableValue", render: (value: number | string) => right(money(value)) },
  { title: <HeaderCell title="Tax" align="right" />, dataIndex: "taxAmount", key: "taxAmount", render: (value: number | string) => right(money(value)) },
  { title: <HeaderCell title="Line total" align="right" />, dataIndex: "lineTotal", key: "lineTotal", render: (value: number | string) => right(money(value)) },
];

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Text className="mb-2 text-sm font-semibold">{label}</Text>
      <Text>{children}</Text>
    </div>
  );
}

function Absent({ children }: { children: ReactNode }) {
  return (
    <Text as="span" className="italic text-gray-500">
      {children}
    </Text>
  );
}

export function CreditNoteDocument({
  data,
  irnState,
  children,
}: {
  data: CreditNoteDocumentData;
  /** Computed by the caller from its own IRN-status map; `undefined` falls back to the raw word. */
  irnState?: ComplianceState;
  /** Print controls, back links — anything that must not reach paper. Marked no-print. */
  children?: ReactNode;
}) {
  return (
    <>
      {children ? (
        <div data-print="hide" className="flex flex-wrap gap-2 p-4">
          {children}
        </div>
      ) : null}

      <article data-slot="invoice-document" aria-label="Credit note" className="w-full rounded-xl border border-muted p-5 text-sm sm:p-6 lg:p-8">
        <div className="mb-10 flex flex-col-reverse items-start justify-between gap-4 md:flex-row">
          <div>
            <Title as="h1">{data.supplierLegalName ?? "Infra Junction"}</Title>
            <Text className="mt-0.5 text-gray-500">GST credit note</Text>
          </div>
          <div>
            <div className="mb-3 md:mb-2">
              {irnState ? (
                <ComplianceStatusBadge subject="irn" state={irnState} />
              ) : (
                <Badge variant="outline" rounded="md">
                  {words(data.irnStatus)}
                </Badge>
              )}
            </div>
            <Title as="h1">{data.creditNoteNumber}</Title>
            <Text className="mt-0.5 text-gray-500">Credit note number · FY {data.financialYear}</Text>
          </div>
        </div>

        <div className="mb-10 grid gap-6 sm:grid-cols-2">
          <div className="grid gap-4">
            <Title as="h2" className="font-semibold">
              Supplier
            </Title>
            {data.supplierAddress ? <Text>{data.supplierAddress}</Text> : null}
            <Field label="GSTIN">{data.supplierGstin ?? <Absent>not recorded — awaiting the company GSTIN</Absent>}</Field>
            <Field label="Issued on">{formatDate(data.issuedOn)}</Field>
            {data.invoiceNumber ? <Field label="Against invoice">{data.invoiceNumber}</Field> : null}
          </div>

          <div className="grid gap-4">
            <Title as="h2" className="font-semibold">
              Bill to
            </Title>
            {data.recipientLegalName ? <Text className="text-sm font-semibold uppercase">{data.recipientLegalName}</Text> : null}
            {data.recipientAddress ? <Text>{data.recipientAddress}</Text> : null}
            <Field label="GSTIN">{data.recipientGstin ?? <Absent>none — unregistered recipient</Absent>}</Field>
            <Field label="Place of supply">{data.placeOfSupply}</Field>
          </div>
        </div>

        <LegacyTable
          data={data.lines}
          caption={<span className="sr-only">Credit note lines with HSN, GST rate, returned quantity, taxable value and tax</span>}
          columns={
            isDiscounted(data)
              ? [...lineColumns.slice(0, 5), ...discountColumns, ...lineColumns.slice(5)]
              : lineColumns
          }
          variant="modern"
          rowKey={(record) => record.id}
          scroll={{ x: 900 }}
          className="mb-8"
        />

        <div className="flex flex-col-reverse items-start justify-between gap-6 border-t border-muted pt-8 sm:flex-row">
          <div className="grid max-w-md gap-4 pe-4">
            <Field label="Reason">{data.reason}</Field>
            <Field label="IRN">{data.irn ?? <Absent>none</Absent>}</Field>
            {data.irnQr ? (
              <div>
                <Text className="mb-2 text-sm font-semibold">Signed QR payload</Text>
                {data.irnQr.length <= QR_MAX_LENGTH ? (
                  <QRCodeSVG value={data.irnQr} size={128} role="img" aria-label="IRN QR code" className="mb-2" />
                ) : null}
                <Text className="font-mono text-xs break-all">{data.irnQr}</Text>
              </div>
            ) : null}
          </div>

          <dl className="w-full max-w-sm">
            {(
              [
                ...(isDiscounted(data)
                  ? ([
                      ["Gross value", money(data.grossValue ?? data.taxableValue)],
                      ["Less trade discount", money(data.discount ?? 0)],
                    ] as const)
                  : []),
                ["Taxable value", money(data.taxableValue)],
                ["CGST", money(data.cgst)],
                ["SGST", money(data.sgst)],
                ["IGST", money(data.igst)],
                ["Round off", money(data.roundOff)],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="flex items-center justify-between border-b border-muted py-3.5">
                <dt>{label}</dt>
                <dd className="font-semibold tabular-nums">{value}</dd>
              </div>
            ))}
            <div className="flex items-center justify-between pt-4 text-base font-semibold text-gray-900">
              <dt>Grand total</dt>
              <dd className="tabular-nums">{data.grandTotalDisplay ?? money(data.grandTotal)}</dd>
            </div>
          </dl>
        </div>
      </article>
    </>
  );
}
